import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { QuoteRequest } from 'src/app/core/models/itinerary.model';
import { IOsiWaiverTourcodeConfig } from 'src/app/core/models/agency-settings.model';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { Router } from '@angular/router';
import { OSIS_VALIDATORS, PAGETYPE, TOURCODE_VALIDATORS } from 'src/app/core/enums/enums';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';

const osisName = 'osis';

@Component({
  selector: 'app-waiver-osi',
  templateUrl: './waiver-osi.component.html',

  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WaiverOsiComponent implements OnInit, OnDestroy {
  @Input() waiverOptional = false;
  @Input() quoteRequest: QuoteRequest;
  @Input() isAirline: boolean = false;

  @Input() showWaiverEnabled: boolean = false;
  @Input() showTourcodeEnabled: boolean = false;
  @Input() showOsiEnabled: boolean = false;
  @Input() isException: boolean = false;

  @Output() waiverData = new EventEmitter<FormGroup>();
  @Output() valid = new EventEmitter<boolean>();

  subscriptions = new Subscription();

  form: FormGroup;
  submitted = false;
  aditionalFieldsConfig: IOsiWaiverTourcodeConfig = null;
  isInvoluntary: boolean = false;
  showApinValidationAlert = false;

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private router: Router
  ) {}

  ngOnInit() {
    this.isInvoluntary = this.router.url.includes(PAGETYPE.involuntaryChange);
    this.buildForm();
    this.requiredWaiverOsiValidators();
    this.callComponentSubcriptions();
  }

  callComponentSubcriptions(): void {
    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        this.waiverData.emit(this.form);
      })
    );

    this.subscriptions.add(
      this.settingsService.getClientSettings().subscribe((settings) => {
        this.aditionalFieldsConfig = this.isInvoluntary
          ? {
              osiEnabled: settings?.exchangeAdditionalFields?.involuntary?.osiEnabled,
              waiverEnabled: settings?.exchangeAdditionalFields?.involuntary?.waiverEnabled,
              tourcodeEnabled: settings?.exchangeAdditionalFields?.involuntary?.tourcodeEnabled
            }
          : {
              osiEnabled: settings?.exchangeAdditionalFields?.voluntary?.osiEnabled,
              waiverEnabled: settings?.exchangeAdditionalFields?.voluntary?.waiverEnabled,
              tourcodeEnabled: settings?.exchangeAdditionalFields?.voluntary?.tourcodeEnabled
            };
      })
    );
  }

  buildForm() {
    this.form = this.fb.group({
      waiver: this.quoteRequest?.waiver ? [this.quoteRequest?.waiver] : [''],
      tourCode: this.quoteRequest?.tourCode,
      osis: this.fb.array(this.quoteRequest?.osis ? this.quoteRequest?.osis : [])
    });
  }

  requiredWaiverOsiValidators() {
    this.form.controls.waiver.setValidators([Validators.required]);
    this.form.controls.tourCode.setValidators([Validators.required]);
    this.form.controls.osis.setValidators([Validators.required]);
    this.waiverData.emit(this.form);
  }

  osis(): FormArray {
    return this.form.get(osisName) as FormArray;
  }

  addOsi(value: string) {
    const osis = this.form.controls.osis as FormArray;

    if (value) {
      !!value && osis.push(this.fb.control(value));
      this.valid.emit(true);
    }
  }

  validateAuthApinCode(osiText: string) {
    return osiText.toLowerCase().startsWith(OSIS_VALIDATORS.APIN);
  }

  // *NOTE: Tourcode validator was disabled (1 of 2 places)
  validateTourcode(): boolean {
    if (this.form.controls.tourCode.value === '' || this.form.controls.tourCode.value === null) {
      this.valid.emit(true);
      return true;
    } else {
      if (this.form.controls.tourCode.value.toLowerCase().startsWith(TOURCODE_VALIDATORS.MXLA)) {
        this.valid.emit(true);
        return true;
      } else {
        this.valid.emit(false);
        return false;
      }
    }
  }

  deleteOsi(index: number) {
    const osis = this.form.controls.osis as FormArray;
    osis.removeAt(index);
  }

  @Input() set onSubmit(value: boolean) {
    this.valid.emit(this.form && this.form.valid);
    this.submitted = value && !this.submitted ? true : this.submitted;
    if (this.form && this.form.valid && value) {
      this.waiverData.emit(this.form);
    }
  }

  showError(control: FormControl) {
    return this.submitted && control.errors && control.errors.required;
  }

  setWaiverValidators() {
    if (this.waiverOptional) {
      this.form.controls.waiver.setValidators(null);
      return;
    }
    this.subscriptions.add(
      this.form.controls.tourCode.valueChanges.subscribe((value) => {
        if (!!value) {
          this.form.controls.waiver.setValidators(null);
        } else {
          this.form.controls.waiver.setValidators([Validators.required]);
        }
        this.form.controls.waiver.updateValueAndValidity();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
